import React, { useCallback, useEffect, useRef, useState } from "react";
import { GoogleMap, Circle, useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import { Input } from "./ui/input";
import { MapPin, Crosshair, Search, Loader2, Lock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const LIBS = ["places", "drawing"];
const mapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const defaultCenter = { lat: 19.4326, lng: -99.1332 }; // CDMX fallback

// Uber-eats inspired minimal light style — low saturation roads, simplified
// geometry, hidden POI clutter, soft water/parks.
const UBER_LIGHT_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6b6b" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "administrative.land_parcel", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5f3df" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#ffd166" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#e6bf5c" }] },
  { featureType: "road.local", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c8dff6" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#6b9bd2" }] },
];

export default function MapPicker({
  value,
  onChange,
  radiusKm,
  circleColor = "#0A0A0A",
  fallbackCenter,          // {lat, lng} — e.g. company supply point
  serviceAreaPolygons,     // [[[lat,lng], ...], ...] — opcional, dibuja polígonos de cobertura
}) {
  const [center, setCenter] = useState(
    value?.lat
      ? { lat: value.lat, lng: value.lng }
      : fallbackCenter?.lat
        ? { lat: fallbackCenter.lat, lng: fallbackCenter.lng }
        : defaultCenter,
  );
  const [marker, setMarker] = useState(value?.lat ? { lat: value.lat, lng: value.lng } : null);
  const [address, setAddress] = useState(value?.full_address || "");
  const [query, setQuery] = useState(value?.full_address || "");
  const [locating, setLocating] = useState(false);
  const [permissionBlocked, setPermissionBlocked] = useState(false);
  const autocompleteRef = useRef(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: mapKey || "",
    libraries: LIBS,
  });

  useEffect(() => {
    if (value?.lat && value?.lng) {
      setCenter({ lat: value.lat, lng: value.lng });
      setMarker({ lat: value.lat, lng: value.lng });
    }
    if (value?.full_address !== undefined) {
      setAddress(value.full_address || "");
      setQuery(value.full_address || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.lat, value?.lng, value?.full_address]);

  // Geolocation → falls back to `fallbackCenter` → then to defaultCenter.
  useEffect(() => {
    if (marker) return;
    const applyFallback = () => {
      if (fallbackCenter?.lat && fallbackCenter?.lng) {
        setCenter({ lat: fallbackCenter.lat, lng: fallbackCenter.lng });
      }
    };
    if (!("geolocation" in navigator)) {
      applyFallback();
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        setMarker(c);
        reverseGeocode(c);
      },
      applyFallback,
      { timeout: 5000, enableHighAccuracy: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = useCallback((coords, addr) => {
    onChange?.({ lat: coords.lat, lng: coords.lng, full_address: addr !== undefined ? addr : address });
  }, [onChange, address]);

  // Dibujo imperativo de polígonos de cobertura cuando la empresa los
  // tiene definidos. Mismo patrón que TrackingMap (evita leaks por
  // unmount conditional de <Polygon /> de @react-google-maps/api).
  // No hacemos auto-fit aquí porque el "pin" del MapPicker es un
  // overlay CSS pegado al centro visual del mapa (estilo Uber), no un
  // Marker real ligado a coordenadas. Hacer zoom-out movería el pin
  // visualmente al centro de la nueva vista, alejándolo de la dirección
  // que el usuario seleccionó. El polígono se dibuja en su posición
  // geográfica real; si queda fuera del viewport actual, el banner
  // ámbar avisa al usuario y puede usar el zoom-out manual del mapa.
  useEffect(() => {
    if (!isLoaded || !mapReady || !mapRef.current || !window.google?.maps) return;
    if (!serviceAreaPolygons || serviceAreaPolygons.length === 0) return;
    const polys = serviceAreaPolygons.map((ring) => new window.google.maps.Polygon({
      paths: (ring || []).map(([lat, lng]) => ({ lat, lng })),
      strokeColor: circleColor,
      strokeOpacity: 0.85,
      strokeWeight: 2,
      fillColor: circleColor,
      fillOpacity: 0.12,
      clickable: false,
      map: mapRef.current,
    }));
    return () => {
      polys.forEach((p) => { try { p.setMap(null); } catch (_e) { /* ignore */ } });
    };
  }, [isLoaded, mapReady, serviceAreaPolygons, circleColor]);

  const reverseGeocode = useCallback((coords) => {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: coords }, (results, status) => {
      if (status === "OK" && results?.[0]) {
        const fa = results[0].formatted_address;
        setAddress(fa);
        setQuery(fa);
        emit(coords, fa);
      }
    });
  }, [emit]);

  const onMapClick = useCallback((e) => {
    const c = { lat: e.latLng.lat(), lng: e.latLng.lng() };
    setMarker(c);
    setCenter(c);
    emit(c);
    reverseGeocode(c);
  }, [emit, reverseGeocode]);

  // Uber-style: the marker tracks the map center. When the user stops panning,
  // we commit the new center as the picked location.
  const onIdle = useCallback(() => {
    const m = mapRef.current;
    if (!m) return;
    const c = m.getCenter();
    if (!c) return;
    const coords = { lat: c.lat(), lng: c.lng() };
    if (marker && Math.abs(marker.lat - coords.lat) < 1e-6 && Math.abs(marker.lng - coords.lng) < 1e-6) return;
    setMarker(coords);
    emit(coords);
    // Debounce reverse geocode a bit: immediate is fine for UX.
    reverseGeocode(coords);
  }, [marker, emit, reverseGeocode]);

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace();
    if (!place?.geometry) return;
    const c = { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() };
    setCenter(c);
    setMarker(c);
    const fa = place.formatted_address || place.name || "";
    setAddress(fa);
    setQuery(fa);
    emit(c, fa);
  };

  const recenterOnMe = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!("geolocation" in navigator)) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    // Check permission state when API available — gives clearer error messages.
    try {
      if (navigator.permissions?.query) {
        const perm = await navigator.permissions.query({ name: "geolocation" });
        if (perm.state === "denied") {
          // Permission previously denied — JS can NOT re-prompt. Show in-app help.
          setPermissionBlocked(true);
          toast.error("Permiso de ubicación bloqueado. Sigue las instrucciones sobre el mapa.");
          return;
        }
      }
    } catch (_) {
      /* permissions API optional */
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        setMarker(c);
        reverseGeocode(c);
        if (mapRef.current) mapRef.current.panTo(c);
        setLocating(false);
        setPermissionBlocked(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          // Fallback if Permissions API isn't available (Safari iOS <16 etc.).
          setPermissionBlocked(true);
          toast.error("Permiso de ubicación bloqueado. Sigue las instrucciones sobre el mapa.");
          return;
        }
        const msg =
          err.code === err.POSITION_UNAVAILABLE
            ? "No pudimos obtener tu ubicación. Revisa tu GPS."
            : err.code === err.TIMEOUT
              ? "Tiempo de espera agotado. Intenta de nuevo."
              : "No pudimos obtener tu ubicación";
        toast.error(msg);
      },
      { timeout: 10000, enableHighAccuracy: true, maximumAge: 0 },
    );
  };

  if (!mapKey) {
    return (
      <div className="space-y-3">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Google Maps aún no está configurado. Ingresa la dirección manualmente por ahora.
        </div>
        <Input
          data-testid="map-address-input"
          placeholder="Dirección completa"
          value={address}
          onChange={(e) => {
            setAddress(e.target.value);
            emit({ lat: 0, lng: 0 }, e.target.value);
          }}
          className="h-12"
        />
      </div>
    );
  }

  if (loadError) return <div className="text-sm text-red-600">Error cargando Google Maps.</div>;
  if (!isLoaded) return <div className="h-72 bg-neutral-100 rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-3">
      <Autocomplete
        onLoad={(ac) => { autocompleteRef.current = ac; }}
        onPlaceChanged={onPlaceChanged}
        options={{
          fields: ["formatted_address", "geometry", "name"],
          types: ["geocode", "establishment"],
          // Prefer results near current center.
          bounds: center ? new window.google.maps.Circle({ center, radius: 30000 }).getBounds() : undefined,
          strictBounds: false,
        }}
      >
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <Input
            data-testid="map-search-input"
            placeholder="Busca tu calle, colonia o negocio..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-12 pl-9 pr-10 rounded-xl shadow-sm"
          />
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={recenterOnMe}
            disabled={locating}
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 flex items-center justify-center rounded-lg text-[color:var(--company-primary)] hover:bg-[color:var(--company-primary)]/10 disabled:opacity-60 tap-fade"
            aria-label="Usar mi ubicación"
            data-testid="map-recenter-btn"
          >
            {locating ? <Loader2 size={16} className="animate-spin" /> : <Crosshair size={16} />}
          </button>
        </div>
      </Autocomplete>

      <div className="relative rounded-2xl overflow-hidden border border-neutral-200 shadow-sm h-[280px] sm:h-[360px]">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={center}
          zoom={radiusKm ? Math.max(10, 14 - Math.floor(Math.log2(Math.max(radiusKm, 1)))) : 16}
          onClick={onMapClick}
          onLoad={(m) => { mapRef.current = m; setMapReady(true); }}
          onIdle={onIdle}
          options={{
            disableDefaultUI: true,
            zoomControl: true,
            zoomControlOptions: { position: 7 }, // RIGHT_BOTTOM
            clickableIcons: false,
            gestureHandling: "greedy",
            styles: UBER_LIGHT_STYLE,
          }}
        >
          {marker && radiusKm > 0 && (
            <Circle
              center={marker}
              radius={radiusKm * 1000}
              options={{
                strokeColor: circleColor,
                strokeOpacity: 0.5,
                strokeWeight: 1.5,
                fillColor: circleColor,
                fillOpacity: 0.08,
                clickable: false,
              }}
            />
          )}
        </GoogleMap>

        {/* Uber-style centered floating pin — position tracks map center.
            3D look: tear-drop shape (rounded head + pointed tip via skewed
            inner square), soft elliptical ground shadow, and a pulsing ring
            around the head to hint that the pin is "alive". */}
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center z-20">
          <div className="relative flex flex-col items-center -translate-y-4">
            {/* Head with pulsing ring */}
            <div className="relative" style={{ color: circleColor }}>
              <span className="absolute inset-0 rounded-full map-pin-pulse" aria-hidden />
              <div
                className="relative h-12 w-12 rounded-full flex items-center justify-center text-white ring-2 ring-white"
                style={{
                  backgroundColor: circleColor,
                  boxShadow: `0 4px 10px rgba(0,0,0,0.25), inset 0 -3px 6px rgba(0,0,0,0.2), inset 0 3px 6px rgba(255,255,255,0.25)`,
                }}
              >
                <MapPin size={22} fill="white" strokeWidth={2.5} />
              </div>
              {/* Pointed tip below head — diamond rotated 45°, overlapping bottom */}
              <div
                className="absolute left-1/2 -bottom-1.5 -translate-x-1/2 w-4 h-4 rotate-45 ring-2 ring-white"
                style={{
                  backgroundColor: circleColor,
                  boxShadow: `0 4px 6px rgba(0,0,0,0.2)`,
                }}
                aria-hidden
              />
            </div>
            {/* Soft ground shadow (ellipse) — faded blur under the tip */}
            <div
              className="mt-2 h-1.5 w-8 rounded-full bg-black/35 blur-[2px] map-pin-shadow"
              aria-hidden
            />
          </div>
        </div>

        {/* Permission-blocked help panel (overlays the map) */}
        {permissionBlocked && (
          <PermissionBlockedOverlay
            onRetry={() => recenterOnMe()}
            onDismiss={() => setPermissionBlocked(false)}
            retrying={locating}
          />
        )}
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2">
        <MapPin size={14} className="text-company shrink-0" />
        <p className="text-xs text-neutral-700 truncate flex-1" data-testid="map-selected-address">
          {address || "Toca el mapa o busca para fijar tu punto exacto"}
        </p>
      </div>
    </div>
  );
}

// ------- Permission-blocked help overlay -------
function detectBrowser() {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isFirefox = /Firefox\//i.test(ua);
  const isEdge = /Edg\//i.test(ua);
  const isSafari = /Safari\//i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua);
  const isChrome = /Chrome\//i.test(ua) && !isEdge;
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  if (isFirefox) return "firefox";
  if (isEdge) return "edge";
  if (isSafari) return "safari";
  if (isChrome) return "chrome";
  return "desktop";
}

const INSTRUCTIONS = {
  chrome: [
    "Haz click en el ícono de candado 🔒 en la barra de direcciones (arriba).",
    "Selecciona \"Configuración del sitio\" o \"Permisos\".",
    "En \"Ubicación\", elige \"Permitir\".",
    "Vuelve aquí y toca \"Reintentar\".",
  ],
  edge: [
    "Haz click en el candado 🔒 junto a la URL.",
    "Abre \"Permisos para este sitio\".",
    "Cambia \"Ubicación\" a \"Permitir\".",
    "Toca \"Reintentar\" abajo.",
  ],
  firefox: [
    "Haz click en el candado 🔒 de la barra de direcciones.",
    "Busca \"Acceso a tu ubicación\" y quita el bloqueo.",
    "Vuelve y toca \"Reintentar\".",
  ],
  safari: [
    "En el menú Safari → Ajustes → Sitios web → Ubicación.",
    "Busca este sitio y cámbialo a \"Permitir\".",
    "Regresa y toca \"Reintentar\".",
  ],
  ios: [
    "Toca el botón \"aA\" a la izquierda de la URL.",
    "Elige \"Ajustes del sitio web\".",
    "Cambia \"Ubicación\" a \"Permitir\".",
    "Vuelve y toca \"Reintentar\".",
  ],
  android: [
    "Toca el candado 🔒 junto a la URL.",
    "Entra a \"Permisos\" y activa \"Ubicación\".",
    "Regresa y toca \"Reintentar\".",
  ],
  desktop: [
    "Busca el candado 🔒 junto a la URL.",
    "Abre los permisos del sitio y permite \"Ubicación\".",
    "Toca \"Reintentar\".",
  ],
};

function PermissionBlockedOverlay({ onRetry, onDismiss, retrying }) {
  const [browser] = useState(() => detectBrowser());
  const steps = INSTRUCTIONS[browser] || INSTRUCTIONS.desktop;
  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-[2px] p-4">
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-neutral-200 overflow-hidden"
        data-testid="map-permission-blocked"
      >
        <div className="p-4 border-b border-neutral-100 flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Lock size={18} />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-neutral-900">Ubicación bloqueada</h4>
            <p className="text-xs text-neutral-600 mt-0.5">
              Permite el acceso a tu ubicación desde el candado de la URL — no hace falta entrar a los ajustes del celular.
            </p>
          </div>
        </div>
        <ol className="p-4 space-y-2 list-decimal list-inside text-xs text-neutral-700">
          {steps.map((s, i) => (
            <li key={i} className="leading-relaxed">{s}</li>
          ))}
        </ol>
        <div className="p-3 pt-0 flex items-center gap-2">
          <button
            type="button"
            onClick={onRetry}
            disabled={retrying}
            className="flex-1 h-10 rounded-xl bg-black text-white text-sm font-medium hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid="map-permission-retry-btn"
          >
            {retrying ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />}
            Reintentar
          </button>
          <button
            type="button"
            onClick={onDismiss}
            className="h-10 px-3 rounded-xl border border-neutral-200 text-sm text-neutral-700 hover:bg-neutral-50"
            data-testid="map-permission-dismiss-btn"
          >
            Ingresar manual
          </button>
        </div>
        <div className="px-4 pb-3 flex items-center gap-1.5 text-[10px] text-neutral-500">
          <AlertCircle size={11} />
          <span>Si sigues sin verlo, recarga la página tras permitir.</span>
        </div>
      </div>
    </div>
  );
}
